import React, { useState, useRef, useCallback } from 'react';
import { useDesign } from '../../designs';
import { TripMeta } from '../../types/trip';
import { isShareCodeTaken, saveCustomSlug } from '../../services/trips';

interface PostPaymentTourProps {
  tripId: string;
  meta: TripMeta;
  onDismiss: () => void;
}

const TOTAL_SCREENS = 4;

export default function PostPaymentTour({ tripId, meta, onDismiss }: PostPaymentTourProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const [currentScreen, setCurrentScreen] = useState(0);
  const [copied, setCopied] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Slug editing state
  const [editingSlug, setEditingSlug] = useState(false);
  const [slug, setSlug] = useState(meta.customSlug || meta.shareCode);
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState('');

  const currentSlug = meta.customSlug || meta.shareCode;
  const [displaySlug, setDisplaySlug] = useState(currentSlug);
  const shareUrl = `${window.location.origin}/t/${displaySlug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSaveSlug = async () => {
    const cleaned = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!cleaned || cleaned === displaySlug) {
      setEditingSlug(false);
      setSlug(displaySlug);
      return;
    }
    setSlugSaving(true);
    setSlugError('');
    try {
      const taken = await isShareCodeTaken(cleaned);
      if (taken) { setSlugError('Taken'); setSlugSaving(false); return; }
      const oldSlug = meta.customSlug || meta.shareCode;
      await saveCustomSlug(tripId, cleaned, oldSlug, meta.shareCode);
      setDisplaySlug(cleaned);
      setEditingSlug(false);
    } catch (err) {
      console.error('Failed to save slug:', err);
      setSlugError('Error');
    } finally {
      setSlugSaving(false);
    }
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentScreen < TOTAL_SCREENS - 1) setCurrentScreen(s => s + 1);
      if (diff < 0 && currentScreen > 0) setCurrentScreen(s => s - 1);
    }
  }, [currentScreen]);

  const next = () => {
    if (currentScreen < TOTAL_SCREENS - 1) setCurrentScreen(s => s + 1);
  };

  // ── Shared illustrative styles ──
  const mockRowStyle: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${d.border}`,
  };
  const mockNameStyle: React.CSSProperties = {
    fontFamily: "'Raleway', sans-serif", fontSize: fs.body, fontWeight: 500,
    color: d.textPrimary,
  };
  const mockDetailStyle: React.CSSProperties = {
    fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
    color: d.textSecondary,
  };
  const mockAmountStyle: React.CSSProperties = {
    fontFamily: displayFont, fontSize: fs.body, fontWeight: 700,
    color: d.accent, textAlign: 'right' as const,
  };
  const expenseRowStyle: React.CSSProperties = {
    borderLeft: '2px solid rgba(200,146,58,0.22)',
    padding: '10px 14px',
    borderBottom: `1px solid ${d.border}`,
  };

  const screens = [
    // ── SCREEN 1: TRAVEL ──
    <div key="travel">
      <h2 style={{
        fontFamily: displayFont, fontSize: '20px', fontWeight: 700,
        color: d.textPrimary, textAlign: 'center', margin: '0 0 12px',
        // 20px display heading retained inline (between bodyLarge and screenHeader)
      }}>Your crew's travel, all in one place</h2>
      <p style={{
        fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
        color: d.textSecondary, textAlign: 'center', lineHeight: 1.6,
        margin: '0 0 24px',
      }}>When your crew adds their travel, everyone knows who's arriving when.</p>

      <div style={{
        backgroundColor: d.bgSurface, border: `1px solid ${d.border}`,
        borderRadius: '4px', overflow: 'hidden',
      }}>
        <div style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
          letterSpacing: '3px', textTransform: 'uppercase',
          color: d.accent, padding: '10px 12px 6px',
        }}>ARRIVALS</div>
        <div style={mockRowStyle}>
          <div>
            <div style={mockNameStyle}>Sarah</div>
            <div style={mockDetailStyle}>SFO · UA 1482 · 2:30 PM</div>
          </div>
          <div style={{ ...mockDetailStyle, textAlign: 'right' }}>On-site 4:00 PM</div>
        </div>
        <div style={mockRowStyle}>
          <div>
            <div style={mockNameStyle}>Marcus</div>
            <div style={mockDetailStyle}>LAX · DL 892 · 11:15 AM</div>
          </div>
          <div style={{ ...mockDetailStyle, textAlign: 'right' }}>On-site 1:00 PM</div>
        </div>
        <div style={{ ...mockRowStyle, borderBottom: 'none' }}>
          <div>
            <div style={mockNameStyle}>Jamie</div>
            <div style={{ ...mockDetailStyle, fontStyle: 'italic' }}>Driving from Portland</div>
          </div>
          <div style={{ ...mockDetailStyle, textAlign: 'right' }}>On-site 3:30 PM</div>
        </div>
      </div>
    </div>,

    // ── SCREEN 2A: EXPENSES ──
    <div key="expenses">
      <h2 style={{
        fontFamily: displayFont, fontSize: '20px', fontWeight: 700,
        color: d.textPrimary, textAlign: 'center', margin: '0 0 12px',
        // 20px display heading retained inline (between bodyLarge and screenHeader)
      }}>Log it. Split it. Done.</h2>
      <p style={{
        fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
        color: d.textSecondary, textAlign: 'center', lineHeight: 1.6,
        margin: '0 0 24px',
      }}>Log shared expenses and the app handles the math. No spreadsheets, no awkward texts.</p>

      <div style={{
        backgroundColor: d.bgSurface, border: `1px solid ${d.border}`,
        borderRadius: '4px', overflow: 'hidden',
      }}>
        <div style={expenseRowStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={mockNameStyle}>Dinner at Chez Panisse</span>
            <span style={mockAmountStyle}>$240</span>
          </div>
          <div style={mockDetailStyle}>Sarah paid · Split 4 ways</div>
        </div>
        <div style={expenseRowStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={mockNameStyle}>Uber to vineyard</span>
            <span style={mockAmountStyle}>$45</span>
          </div>
          <div style={mockDetailStyle}>Marcus paid · Split 4 ways</div>
        </div>
        <div style={{ ...expenseRowStyle, borderBottom: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={mockNameStyle}>Groceries</span>
            <span style={mockAmountStyle}>$62</span>
          </div>
          <div style={mockDetailStyle}>Jamie paid · Split 4 ways</div>
        </div>
      </div>
    </div>,

    // ── SCREEN 2B: SETTLE UP ──
    <div key="settle">
      <h2 style={{
        fontFamily: displayFont, fontSize: '20px', fontWeight: 700,
        color: d.textPrimary, textAlign: 'center', margin: '0 0 12px',
        // 20px display heading retained inline (between bodyLarge and screenHeader)
      }}>One tap to settle up</h2>
      <p style={{
        fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
        color: d.textSecondary, textAlign: 'center', lineHeight: 1.6,
        margin: '0 0 24px',
      }}>At the end of the trip, everyone sees exactly what they owe — and to whom.</p>

      <div style={{
        backgroundColor: d.bgSurface, border: `1px solid ${d.border}`,
        borderRadius: '4px', overflow: 'hidden',
      }}>
        <div style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
          letterSpacing: '3px', textTransform: 'uppercase',
          color: d.accent, padding: '10px 12px 6px',
        }}>SETTLE UP</div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', borderBottom: `1px solid ${d.border}`,
        }}>
          <span style={mockNameStyle}>Jamie → Sarah</span>
          <span style={mockAmountStyle}>$48.75</span>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', borderBottom: `1px solid ${d.border}`,
        }}>
          <span style={mockNameStyle}>Jamie → Marcus</span>
          <span style={mockAmountStyle}>$15.00</span>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', opacity: 0.55,
        }}>
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.body, fontWeight: 500,
            color: 'rgba(237,228,216,0.50)', textDecoration: 'line-through',
          }}>Marcus → Sarah</span>
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 500,
            letterSpacing: '1.5px', textTransform: 'uppercase',
            color: d.confirmed,
          }}>Settled ✓</span>
        </div>
      </div>

      {/* KREWE fee callout */}
      <div style={{
        backgroundColor: d.bgSurface, border: `1px dashed ${d.textSecondary}`,
        borderRadius: '4px', padding: '12px 14px', marginTop: '20px',
      }}>
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary, margin: 0, lineHeight: 1.5,
        }}>
          We've added a reminder to log the $30 KREWE fee as a shared expense. Add it, skip it, or split it however you like — your call.
        </p>
      </div>
    </div>,

    // ── SCREEN 3: SHARE ──
    <div key="share">
      <h2 style={{
        fontFamily: displayFont, fontSize: '20px', fontWeight: 700,
        color: d.textPrimary, textAlign: 'center', margin: '0 0 12px',
        // 20px display heading retained inline (between bodyLarge and screenHeader)
      }}>You're live!</h2>
      <p style={{
        fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
        color: d.textSecondary, textAlign: 'center', lineHeight: 1.6,
        margin: '0 0 36px',
      }}>Text it, drop it in the group chat, send a carrier pigeon — your crew is one tap away.</p>

      {/* Share link field — 12px extra spacing after subtext */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '10px 12px', backgroundColor: d.bgSurface,
        border: `1px solid ${d.border}`, borderRadius: '2px',
        marginBottom: '8px',
      }}>
        {editingSlug ? (
          <>
            <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, color: d.textSecondary, flexShrink: 0 }}>/t/</span>
            <input type="text" value={slug}
              onChange={(e) => { setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); setSlugError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveSlug()}
              autoFocus
              style={{
                flex: 1, fontFamily: "'Raleway', sans-serif", fontSize: fs.detail,
                color: d.textPrimary, backgroundColor: d.bgSurface,
                border: `1px solid ${slugError ? d.negative : 'rgba(200,146,58,0.30)'}`,
                borderRadius: '2px', padding: '4px 8px', outline: 'none', minWidth: 0,
              }}
            />
            {slugError && <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, color: d.negative }}>{slugError}</span>}
            <button onClick={handleSaveSlug} disabled={slugSaving} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase',
              color: d.confirmed, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}>{slugSaving ? '...' : 'Save'}</button>
            <button onClick={() => { setEditingSlug(false); setSlug(displaySlug); setSlugError(''); }} style={{
              fontSize: fs.bodyInput, color: d.textSecondary, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}>×</button>
          </>
        ) : (
          <>
            <span style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, color: d.textPrimary,
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{shareUrl}</span>
            <button onClick={handleCopy} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase',
              color: copied ? d.confirmed : d.accent,
              background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}>{copied ? 'Copied' : 'Copy'}</button>
          </>
        )}
      </div>

      {/* Edit link toggle */}
      {!editingSlug && (
        <button onClick={() => { setEditingSlug(true); setSlug(displaySlug); }} style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary, background: 'none', border: 'none',
          cursor: 'pointer', display: 'block', margin: '0 auto 4px',
        }}>Edit link</button>
      )}
    </div>,
  ];

  const isLastScreen = currentScreen === TOTAL_SCREENS - 1;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          width: '100%', maxWidth: '400px',
          padding: '32px 24px 24px',
          backgroundColor: d.bgPrimary,
          borderRadius: '4px',
          border: `1px solid ${d.border}`,
          borderTop: `1px solid ${d.borderEmphasis}`,
          position: 'relative',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Skip link */}
        <button onClick={onDismiss} style={{
          position: 'absolute', top: '12px', right: '16px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 300,
          color: d.textSecondary, background: 'none', border: 'none',
          cursor: 'pointer', zIndex: 1,
        }}>Skip</button>

        {/* Screen content */}
        {screens[currentScreen]}

        {/* Navigation */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          marginTop: '28px', gap: '16px',
        }}>
          {isLastScreen ? (
            /* DONE — full-width tier 2 gold border pill */
            <button onClick={onDismiss} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '3px', textTransform: 'uppercase',
              color: d.accent, background: 'transparent',
              border: `1px solid ${d.borderEmphasis}`,
              borderRadius: `${design.options.cardBorderRadius}px`,
              padding: '11px 20px', cursor: 'pointer', width: '100%',
              lineHeight: 1,
            }}>
              <span style={{ marginRight: '6px', fontSize: '7px' }}>◆</span>
              DONE
              <span style={{ marginLeft: '6px', fontSize: '7px' }}>◆</span>
            </button>
          ) : (
            /* NEXT → text link */
            <button onClick={next} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: '9.5px', fontWeight: 700,
              letterSpacing: '2.5px', textTransform: 'uppercase',
              color: d.accent, background: 'none', border: 'none',
              cursor: 'pointer', padding: '8px 16px',
            }}>NEXT →</button>
          )}

          {/* Diamond dot indicators */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {Array.from({ length: TOTAL_SCREENS }, (_, i) => (
              <div key={i} style={{
                width: '6px', height: '6px',
                backgroundColor: i === currentScreen ? d.accent : 'transparent',
                border: i === currentScreen ? 'none' : `1px solid ${d.textTertiary}`,
                transform: 'rotate(45deg)',
                transition: 'background-color 0.2s',
              }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
